import { LayersDataTypes } from "../types/layers-data-types";
import { setLayersVisible } from "../services/reducers/map";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { Layer } from "../interfaces/layer";
import {
  ConvertedKrookie,
  FormDataEditDetails,
  HistoryLog,
  OldPlot,
} from "../interfaces/krookie";
import { formatDateWithoutTime } from "./formatDate";
import Polygon from "@arcgis/core/geometry/Polygon";
import { LandPlot } from "../interfaces/card";
import { convertPolygonToString, getSpatialByWkid } from "./master-plan-helper";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import { planarArea } from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";

export const getNewChangeNumber = (
  historyLog: Array<{ changeNum: string }>,
): string => {
  if (historyLog.length === 0) return "001";
  const numbers = historyLog
    .map((item) => parseInt(item.changeNum, 10))
    .filter((num) => !isNaN(num));
  if (numbers.length === 0) return "001";
  const maxNumber = Math.max(...numbers);
  const newNumber = maxNumber + 1;
  const maxDigits = Math.max(
    ...historyLog.map((item) => item.changeNum.length),
  );
  return newNumber.toString().padStart(Math.max(3, maxDigits), "0");
};

export const safeParse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const updateVisibleLayers = (
  layersIds: Record<string, number[]>,
  visibleLayersId: number[],
  dispatch: Function,
) => {
  const missingLayerIds = [
    LayersDataTypes.Plots,
    LayersDataTypes.InvestmentOpportunities,
    LayersDataTypes.CityBoundary,
  ].flatMap((type) => {
    const typeIds = layersIds[type] || [];
    return typeIds.some((id) => visibleLayersId.includes(id)) ? [] : typeIds;
  });
  if (missingLayerIds.length) {
    dispatch(
      setLayersVisible(
        Array.from(new Set([...visibleLayersId, ...missingLayerIds])),
      ),
    );
  }
};

export async function addPlotFromKrookieDataToArcgis(
  layers: Layer[],
  convertedKrookieData: ConvertedKrookie,
) {
  const foundCityLayers = layers.filter(
    (layer) => layer.dataType === LayersDataTypes.CityBoundary,
  );
  const foundPlotsLayers = layers.filter(
    (layer) => layer.dataType === LayersDataTypes.Plots,
  );

  const krookieGeometry = convertedKrookieData.geometry as Polygon;

  const krookieObject = new Polygon({
    rings: krookieGeometry.rings,
    spatialReference: krookieGeometry.spatialReference,
  });

  for (const layer of foundCityLayers) {
    if (!layer.url) continue;

    const featureLayer = new FeatureLayer({
      url: layer?.url,
      definitionExpression: layer.style2D?.query,
    });

    await featureLayer.load();

    const cityResult = await featureLayer.queryFeatures({
      where: `estateId = ${convertedKrookieData.cityEn.value}`,
      returnGeometry: true,
    });

    if (cityResult.features.length === 0) continue;

    const allCityPlots = [];
    let containingCityGeometry = null;

    for (const cityFeature of cityResult.features) {
      const cityGeometry = cityFeature.geometry;

      const isGeometryInCity = geometryEngine.contains(
        cityGeometry,
        krookieObject,
      );

      if (!isGeometryInCity) {
        continue;
      }

      containingCityGeometry = cityGeometry;

      for (const plotLayer of foundPlotsLayers) {
        if (!plotLayer.url) continue;

        const plotFeatureLayer = new FeatureLayer({
          url: plotLayer.url,
          definitionExpression: plotLayer.style2D?.query,
        });
        await plotFeatureLayer.load();

        const plotsResult = await plotFeatureLayer.queryFeatures({
          geometry: cityGeometry,
          spatialRelationship: "intersects",
          returnGeometry: true,
          outFields: ["*"],
        });

        allCityPlots.push(...plotsResult.features);
      }

      break;
    }

    if (!containingCityGeometry) {
      continue;
    }

    if (allCityPlots.length === 0) {
      for (const plotLayer of foundPlotsLayers) {
        if (!plotLayer.url) continue;

        const plotFeatureLayer = new FeatureLayer({
          url: plotLayer.url,
          definitionExpression: plotLayer.style2D?.query,
        });
        await plotFeatureLayer.load();

        const plotsResult = await plotFeatureLayer.queryFeatures({
          where: `estateID = ${convertedKrookieData.cityEn.value}`,
          returnGeometry: true,
          outFields: ["*"],
        });

        allCityPlots.push(...plotsResult.features);

        if (allCityPlots.length > 0) break;
      }
    }

    if (allCityPlots.length === 0) {
      for (const otherCityLayer of foundCityLayers) {
        if (!otherCityLayer.url) continue;

        const otherFeatureLayer = new FeatureLayer({
          url: otherCityLayer.url,
          definitionExpression: otherCityLayer.style2D?.query,
        });
        await otherFeatureLayer.load();

        const otherCitiesResult = await otherFeatureLayer.queryFeatures({
          where: "1=1",
          returnGeometry: true,
          outFields: ["*"],
        });

        for (const otherCityFeature of otherCitiesResult.features) {
          const otherCityGeometry = otherCityFeature.geometry;
          if (
            otherCityGeometry.spatialReference.wkid !==
            containingCityGeometry.spatialReference.wkid
          ) {
            continue;
          }

          for (const plotLayer of foundPlotsLayers) {
            if (!plotLayer.url) continue;

            const plotFeatureLayer = new FeatureLayer({
              url: plotLayer.url,
              definitionExpression: plotLayer.style2D?.query,
            });
            await plotFeatureLayer.load();

            const plotsResult = await plotFeatureLayer.queryFeatures({
              where: `estateID = ${otherCityFeature.attributes.estateID || otherCityFeature.attributes.estateId}`,
              returnGeometry: true,
              outFields: ["*"],
            });

            if (plotsResult.features.length > 0) {
              allCityPlots.push(...plotsResult.features);
              break;
            }
          }

          if (allCityPlots.length > 0) break;
        }

        if (allCityPlots.length > 0) break;
      }
    }

    if (allCityPlots.length > 0) {
      const firstPlot = allCityPlots[0];
      const attributes = firstPlot.attributes;
      const firstPlotLayer = firstPlot.layer as FeatureLayer;

      const plotLayerUrl = `${firstPlotLayer.url}/${firstPlotLayer.layerId}`;

      const hasArea40 = "DBO.Plots_40.Area" in attributes;
      const hasArea39 = "DBO.Plots_39.Area" in attributes;

      const attributesToArcgis: Record<string, any> = {
        PlotNum: convertedKrookieData.plotNumber,
        Location_Code: convertedKrookieData.plotNumber,
        location_area: Number(convertedKrookieData.area),
        estateID: convertedKrookieData.cityEn.value,
        estateEn: convertedKrookieData.cityEn.value,
        estateAr: convertedKrookieData.cityEn.value,
        is_inv_op: convertedKrookieData.isInvestmentOpportunity ? 1 : 0,
        is_inv_lan: 1,
        status: convertedKrookieData.status.value ?? "",
        location_status_code: convertedKrookieData.locationStatusCode.value,
        Landuse: convertedKrookieData.landUse.value,
        parent_location_Serial: convertedKrookieData.phase.value,
        is_active: 1,
        is_admin_office: 0,
        prodtype_id: "0",
        InOp_nam_a: "0",
        InOp_nam_e: "0",
        InOp_des_a: "0",
        InOp_des_e: "0",
        OBJECTID: 0,
        location_type_code: "land",
      };

      if (hasArea40) {
        attributesToArcgis["DBO.Plots_40.Area"] = Number(
          convertedKrookieData.area,
        );
      } else if (hasArea39) {
        attributesToArcgis["DBO.Plots_39.Area"] = Number(
          convertedKrookieData.area,
        );
      }

      const newPlotGraphic = new Graphic({
        geometry: krookieObject,
        attributes: attributesToArcgis,
      });

      const targetLayer = new FeatureLayer({ url: plotLayerUrl });

      return await addObjectToArcgisLayer(targetLayer, newPlotGraphic);
    }
  }

  return null;
}

export async function updatePlotFromKrookieData(
  layers: Layer[],
  convertedKrookieData: ConvertedKrookie,
  oldPlots: OldPlot[],
) {
  const plotId = oldPlots?.[0]?.plotId;
  if (!plotId || !layers?.length) {
    return;
  }

  const filteredLayers = layers
    .filter(
      (l) =>
        (l.dataType === LayersDataTypes.Plots ||
          l.dataType === LayersDataTypes.InvestmentOpportunities) &&
        l.url,
    )
    .map(
      (l) =>
        new FeatureLayer({
          url: l.url,
          editingEnabled: l.canEdit ?? false,
        }),
    );

  for (const layer of filteredLayers) {
    try {
      await layer.load();
      const { features } = await layer.queryFeatures({
        where: `${layer.objectIdField} = ${plotId}`,
        outFields: ["*"],
      });

      const feature = features[0];

      if (!feature) {
        continue;
      }

      const existingAttributes = feature.attributes;

      const areaKeys = [
        "DBO.Plots_40.Area",
        "DBO.Plots_39.Area",
        "Area",
        "location_area",
      ];

      for (const key of areaKeys) {
        if (Object.prototype.hasOwnProperty.call(existingAttributes, key)) {
          existingAttributes[key] = Number(convertedKrookieData.area);
          break;
        }
      }

      feature.attributes = {
        ...existingAttributes,
        PlotNum: convertedKrookieData.plotNumber,
        estateID: convertedKrookieData.cityEn.value,
        Landuse: convertedKrookieData.landUse.value,
        estateEn: convertedKrookieData.cityEn.value,
        estateAr: convertedKrookieData.cityEn.value,
        parent_location_Serial: convertedKrookieData.phase.value,
        status: convertedKrookieData.status.value ?? "",
        location_status_code: convertedKrookieData.locationStatusCode.value,
        is_inv_op: convertedKrookieData.isInvestmentOpportunity ? 1 : 0,
      };

      feature.geometry = new Polygon({
        rings: (convertedKrookieData.geometry as Polygon).rings,
        spatialReference: convertedKrookieData.geometry.spatialReference,
      });

      await layer.applyEdits({ updateFeatures: [feature] });
      break;
    } catch (error) {
      throw error;
    }
  }
}

export const buildRevision = (historyLog: HistoryLog[]) => {
  const changeNum = getNewChangeNumber(historyLog);
  const currentDate = formatDateWithoutTime(new Date());
  const revision = `${currentDate}_${changeNum}`;
  return { revision, changeNum, currentDate };
};

export const getWkidSelectedCity = async (
  layers: Layer[],
  selectedCityId: number,
) => {
  const foundCityLayers = layers.filter(
    (layer) => layer.dataType === LayersDataTypes.CityBoundary,
  );

  for (const layer of foundCityLayers) {
    if (!layer.url) continue;

    const featureLayer = new FeatureLayer({
      url: layer?.url,
      definitionExpression: layer.style2D?.query,
    });

    await featureLayer.load();

    const result = await featureLayer.queryFeatures({
      where: `estateId = ${selectedCityId}`,
      returnGeometry: true,
    });

    if (result.features.length) {
      return result.features[0].geometry.spatialReference.wkid;
    }
  }

  return null;
};

export const createEmptyLandPlot = (
  wkid: number,
  cityId: number,
  objectId: number = -1,
): LandPlot => {
  const emptyPolygon = new Polygon({
    rings: [],
    spatialReference: { wkid },
  });

  return {
    area: "",
    city: "",
    estateAr: "",
    fid: "",
    geometry: emptyPolygon,
    layer: {} as Layer,
    number: "",
    objectId,
    status: "",
    typeOfUse: "",
    estateEn: cityId,
    landUse: 0,
    phaseId: 0,
    landUseId: 0,
    cityId: cityId,
    type: "Plots",
    locationStatusCode: "",
    phase: 0,
    is_inv_op: 0,
    annualRate: null,
    locationStatusCodeId: 0,
  };
};

export const createLandPlot = (overrides: Partial<LandPlot>): LandPlot => {
  return {
    area: "",
    city: "",
    estateAr: "",
    fid: "",
    geometry: new Polygon({ rings: [], spatialReference: { wkid: 32640 } }),
    layer: {} as Layer,
    number: "",
    objectId: -1,
    status: "",
    typeOfUse: "",
    estateEn: 0,
    landUse: 0,
    phaseId: 0,
    landUseId: 0,
    cityId: 0,
    type: "Plots",
    locationStatusCode: "",
    phase: 0,
    is_inv_op: 0,
    annualRate: null,
    locationStatusCodeId: 0,
    ...overrides,
  };
};

export const transformToKrookiePlot = (plot: LandPlot) => {
  const polygon = plot.geometry as Polygon;
  return {
    ...plot,
    newGeometry: polygon,
    stringGeometry: convertPolygonToString(polygon),
    area: planarArea(polygon, "square-meters").toFixed(2),
    spatialReference: getSpatialByWkid(plot.geometry.spatialReference.wkid),
  };
};

const buildDivideOrMergePlotAttributes = (
  krookieData: ConvertedKrookie,
  sourceAttrs: Record<string, any>[],
  isMerge: boolean,
): Record<string, any> => {
  const attrs: Record<string, any> = {
    PlotNum: krookieData.plotNumber,
    Location_Code: krookieData.plotNumber,
    location_area: Number(krookieData.area),
    estateID: krookieData.cityEn.value,
    estateEn: krookieData.cityEn.value,
    estateAr: krookieData.cityEn.value,
    Landuse: krookieData.landUse.value,
    parent_location_Serial: krookieData.phase.value,
    is_inv_op: krookieData.isInvestmentOpportunity ? 1 : 0,
    status: krookieData.status.value ?? "",
    location_status_code: krookieData.locationStatusCode.value,
    is_active: 1,
    is_admin_office: 0,
    prodtype_id: "0",
    InOp_nam_a: "0",
    InOp_nam_e: "0",
    InOp_des_a: "0",
    InOp_des_e: "0",
    OBJECTID: 0,
    location_type_code: "land",
  };

  if (sourceAttrs.length === 0) return attrs;

  const firstAttr = sourceAttrs[0];

  if ("DBO.Plots_40.Area" in firstAttr) {
    attrs["DBO.Plots_40.Area"] = Number(krookieData.area);
  } else if ("DBO.Plots_39.Area" in firstAttr) {
    attrs["DBO.Plots_39.Area"] = Number(krookieData.area);
  }

  if (isMerge) {
    const uniqueTenantIds = [
      ...new Set(
        sourceAttrs.map((a) => a.tenant_id).filter((id) => id !== null),
      ),
    ];
    const tenant_id = uniqueTenantIds.length === 1 ? uniqueTenantIds[0] : null;
    const tenantAttr =
      tenant_id === null
        ? null
        : sourceAttrs.find((a) => a.tenant_id === tenant_id);

    const statuses = sourceAttrs.map((a) =>
      a.status && typeof a.status === "string" ? a.status.toLowerCase() : null,
    );
    const mergedStatus = statuses.every((s) => s === "vacant" || s == null)
      ? "Vacant"
      : statuses.includes("rented")
        ? "Rented"
        : statuses.includes("booked")
          ? "Booked"
          : "Vacant";

    Object.assign(attrs, {
      tenant_id,
      tenant_name_e: tenantAttr?.tenant_name_e ?? null,
      tenant_name_a: tenantAttr?.tenant_name_a ?? null,
      is_inv_lan: mergedStatus === "Vacant" ? 1 : 0,
      prodtype_e: tenantAttr?.prodtype_e ?? null,
      prodtype_a: tenantAttr?.prodtype_a ?? null,
    });
  } else {
    if ("is_inv_lan" in firstAttr) {
      attrs.is_inv_lan =
        typeof firstAttr.status === "string" &&
        firstAttr.status.toLowerCase() === "vacant"
          ? 1
          : 0;
    }
    [
      "tenant_id",
      "tenant_name_e",
      "tenant_name_a",
      "prodtype_e",
      "prodtype_a",
    ].forEach((key) => {
      if (key in firstAttr) attrs[key] = firstAttr[key];
    });
  }

  return attrs;
}

export const deleteAndAddPlotsInArcgis = async (
  layers: Layer[],
  plotIdsToDelete: number[],
  krookiesToAdd: ConvertedKrookie[],
  isMerge: boolean,
): Promise<Array<number | null>> => {
  if (!plotIdsToDelete?.length || !layers?.length) {
    return krookiesToAdd.map(() => null);
  }

  const filteredLayers = layers
    .filter(
      (l) =>
        (l.dataType === LayersDataTypes.Plots ||
          l.dataType === LayersDataTypes.InvestmentOpportunities) &&
        l.url,
    )
    .map(
      (l) =>
        new FeatureLayer({
          url: l.url,
          editingEnabled: l.canEdit ?? false,
        }),
    );

  const layerResults: Array<{
    layer: FeatureLayer;
    features: Graphic[];
  }> = [];
  for (const layer of filteredLayers) {
    try {
      await layer.load();
      const { features } = await layer.queryFeatures({
        objectIds: plotIdsToDelete,
        outFields: ["*"],
        returnGeometry: false,
      });
      if (features.length > 0) {
        layerResults.push({ layer, features });
      }
    } catch (err) {
      console.error("Failed to query ArcGIS layer, skipping:", err);
    }
  }

  if (layerResults.length === 0) return krookiesToAdd.map(() => null);

  const sourceAttrs = layerResults.flatMap(({ features }) =>
    features.map((f) => f.attributes),
  );

  const addGraphics = krookiesToAdd.map(
    (krookieData) =>
      new Graphic({
        geometry: new Polygon({
          rings: (krookieData.geometry as Polygon).rings,
          spatialReference: krookieData.geometry.spatialReference,
        }),
        attributes: buildDivideOrMergePlotAttributes(
          krookieData,
          sourceAttrs,
          isMerge,
        ),
      }),
  );

  const { layer: primaryLayer, features: primaryFeatures } = layerResults[0];
  const editResult = await primaryLayer.applyEdits({
    deleteFeatures: primaryFeatures,
    addFeatures: addGraphics,
  });

  return editResult.addFeatureResults.map((res: any) =>
    res && !res.error ? (res.objectId ?? null) : null,
  );
}

const addObjectToArcgisLayer = async (
  targetLayer: FeatureLayer,
  graphic: Graphic,
) => {
  await targetLayer.load();
  const editResult = await targetLayer.applyEdits({
    addFeatures: [graphic],
  });
  const addRes = editResult.addFeatureResults?.[0];
  return addRes && !addRes.error ? (addRes.objectId ?? null) : null;
};

export const createMergedKrookiePlot = (selectedPlots: LandPlot[]) => {
  if (selectedPlots.length > 1) {
    const firstPlot = selectedPlots[0];
    const geometries = selectedPlots.map((plot) => plot.geometry);
    const mergedGeometry = geometryEngine.union(geometries);
    const mergedNumber = selectedPlots.map((plot) => plot.number).join(", ");

    const firstPlotPhase = firstPlot.attributes?.parent_location_serial;
    const allSamePhase = selectedPlots.every(
      (plot) => plot.attributes?.parent_location_serial === firstPlotPhase,
    );
    const firstPlotLanduse = firstPlot.attributes?.Landuse;
    const allSameLanduse = selectedPlots.every(
      (plot) => plot.attributes?.Landuse === firstPlotLanduse,
    );

    const mergedPlot: LandPlot = {
      geometry: mergedGeometry,
      number: mergedNumber,
      phase: allSamePhase ? firstPlot.phase : 0,
      phaseId: allSamePhase ? firstPlot.phaseId : 0,
      cityId: firstPlot.cityId,
      city: firstPlot.city,
      estateEn: firstPlot.estateEn,
      estateAr: firstPlot.estateAr,
      area: "0",
      fid: "",
      is_inv_op: 0,
      layer: firstPlot.layer,
      type: "Plots",
      landUse: allSameLanduse ? firstPlot.landUseId : 0,
      attributes: undefined,
      landUseId: allSameLanduse ? firstPlot.landUseId : 0,
      status: "",
      locationStatusCode: "",
      objectId: -1,
      typeOfUse: allSameLanduse ? firstPlot.typeOfUse : "",
      annualRate: null,
      locationStatusCodeId: 0,
    };

    return mergedPlot;
  }

  return null;
};

export const createEmptyFormData = (): FormDataEditDetails => ({
  cityEn: { displayValue: "", value: null },
  phase: { displayValue: "", value: null },
  plotNumber: "",
  oldPlotNumber: "",
  streetNumber: "",
  landUse: { displayValue: "", value: null },
  status: { displayValue: "", value: null },
  locationStatusCode: { displayValue: "", value: null },
  buildArea: "",
  area: "",
  height: "",
  front: "",
  back: "",
  side: "",
  previousKrookieCode: "",
  notes: [],
  qrCodeLink: "",
  cityAr: "",
  isInvestmentOpportunity: false,
});
